import React, { useEffect, useState } from 'react'
import {
  DATES,
  FIELD_TYPES,
  HOURS,
  MONTHS,
  RECURRING_TABLE_FREQUENCY,
  WEEKDAYS,
} from '../../../utility/constants'
import {
  Autocomplete,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { GroupHeader, GroupItems } from './CreateProcessTemplateDialog'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { useAuthContext } from '../../../hooks/useAuthContext'
import adminFieldService from '../../../services/admin/field'
import { toast } from 'react-toastify'
import moment from 'moment'

const RecurringTableSetup = ({ fieldTemplate, derivableFieldTemplates }) => {
  const { user } = useAuthContext()
  const [config, setConfig] = useState({})

  useEffect(() => {
    ;(async () => {
      const config = await adminFieldService.getRecurringTableConfig(user, fieldTemplate.id)
      setConfig(config[0] || {})
    })()
  }, [fieldTemplate])

  const saveChanges = async () => {
    try {
      await adminFieldService.setRecurringTableConfig(user, fieldTemplate.id, {
        ...config,
        table_field_template_id: fieldTemplate.id,
      })
      toast.success('Recurring table setup done!')
    } catch (error) {
      toast.error('Something went wrong!')
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
        <FormControl variant="outlined" size="small" color="primary" style={{ flexGrow: 1 }}>
          <InputLabel id="select-option-label">Select Frequency</InputLabel>
          <Select
            key={config.frequency || 'frequency'}
            fullWidth
            style={{ minWidth: 120 }}
            size="small"
            color="primary"
            labelId="select-option-label"
            value={config.frequency || null}
            onChange={(e) => {
              setConfig({
                ...config,
                frequency: e.target.value,
              })
            }}
            label="Select Frequency"
          >
            {Object.entries(RECURRING_TABLE_FREQUENCY).map(([key, value]) => {
              return <MenuItem value={value}>{key}</MenuItem>
            })}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" color="primary" style={{ flexGrow: 1 }}>
          <InputLabel id="select-option-label">Select Hour</InputLabel>
          <Select
            key={config.hour || 'hour'}
            fullWidth
            style={{ minWidth: 120 }}
            size="small"
            color="primary"
            labelId="select-option-label"
            value={config.hour || null}
            onChange={(e) => {
              setConfig({
                ...config,
                hour: e.target.value,
              })
            }}
            label="Select Hour"
            disabled={!config.frequency}
          >
            {Object.keys(HOURS).map((it) => {
              return <MenuItem value={it}>{HOURS[it]}</MenuItem>
            })}
          </Select>
        </FormControl>
        {config.frequency == RECURRING_TABLE_FREQUENCY.WEEKLY ? (
          <FormControl variant="outlined" size="small" color="primary" style={{ flexGrow: 1 }}>
            <InputLabel id="select-option-label">Select Day</InputLabel>
            <Select
              key={config.day || 'day'}
              fullWidth
              style={{ minWidth: 120 }}
              size="small"
              color="primary"
              labelId="select-option-label"
              value={config.day || null}
              onChange={(e) => {
                setConfig({
                  ...config,
                  day: e.target.value,
                })
              }}
              label="Select Day"
              disabled={config.frequency == RECURRING_TABLE_FREQUENCY.DAILY || !config.frequency}
            >
              {Object.keys(WEEKDAYS).map((it) => {
                return <MenuItem value={it}>{it}</MenuItem>
              })}
            </Select>
          </FormControl>
        ) : (
          <FormControl variant="outlined" size="small" color="primary" style={{ flexGrow: 1 }}>
            <InputLabel id="select-option-label">Select Date</InputLabel>
            <Select
              key={config.day || 'day'}
              fullWidth
              style={{ minWidth: 120 }}
              size="small"
              color="primary"
              labelId="select-option-label"
              value={config.day || null}
              onChange={(e) => {
                setConfig({
                  ...config,
                  day: e.target.value,
                })
              }}
              label="Select Date"
              disabled={config.frequency == RECURRING_TABLE_FREQUENCY.DAILY || !config.frequency}
            >
              {Object.keys(DATES).map((it) => {
                return <MenuItem value={it}>{it}</MenuItem>
              })}
            </Select>
          </FormControl>
        )}
        <FormControl variant="outlined" size="small" color="primary" style={{ flexGrow: 1 }}>
          <InputLabel id="select-option-label">Select Month</InputLabel>
          <Select
            key={config.month || 'month'}
            style={{ minWidth: 120 }}
            size="small"
            color="primary"
            labelId="select-option-label"
            value={config.month || null}
            onChange={(e) => {
              setConfig({
                ...config,
                month: e.target.value,
              })
            }}
            label="Select Month"
            disabled={
              [
                RECURRING_TABLE_FREQUENCY.DAILY,
                RECURRING_TABLE_FREQUENCY.WEEKLY,
                RECURRING_TABLE_FREQUENCY.MONTHLY,
              ].includes(config.frequency) || !config.frequency
            }
          >
            {Object.keys(MONTHS).map((it) => {
              return <MenuItem value={MONTHS[it]}>{it}</MenuItem>
            })}
          </Select>
        </FormControl>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 16, justifyContent: 'space-between' }}>
        <FormControl variant="outlined" size="small" color="primary" style={{ flexGrow: 1 }}>
          <Autocomplete
            style={{ minWidth: 200 }}
            fullWidth
            id="select-derived-field"
            options={derivableFieldTemplates
              .filter((it) => it.data_structure == FIELD_TYPES.DATETIME)
              .sort((a, b) => -b.activity_template_name.localeCompare(a.activity_template_name))}
            groupBy={(option) => option?.activity_template_name}
            size="small"
            getOptionLabel={(item) => item?.field_template_name}
            value={
              derivableFieldTemplates.find(
                (it) => it.id == config?.start_after_field_template_id
              ) || null
            }
            onChange={(_, v) =>
              setConfig({
                ...config,
                start_after_field_template_id: v?.id,
                start_after_constant_value: null,
              })
            }
            renderGroup={(params) => (
              <li key={params.key}>
                <GroupHeader>{params.group}</GroupHeader>
                <GroupItems>{params.children}</GroupItems>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Select Start After Date Time Field"
                color="primary"
              />
            )}
            disabled={!config.frequency}
          />
        </FormControl>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DemoContainer
            components={['DateTimePicker']}
            sx={{ overflow: 'hidden', padding: '6px 0px', marginTop: '-6px' }}
          >
            <DateTimePicker
              placeholder={'Select Start After Date Time'}
              label={'Select Start After Date Time'}
              value={
                config.start_after_constant_value ? moment(config.start_after_constant_value) : null
              }
              format="DD MM YYYY, h:mm A"
              onChange={(v) => {
                setConfig({
                  ...config,
                  start_after_constant_value: v,
                  start_after_field_template_id: null,
                })
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'outlined',
                  color: 'secondary',
                },
              }}
              disabled={!config.frequency}
            />
          </DemoContainer>
        </LocalizationProvider>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 16, justifyContent: 'flex-end' }}>
        <Button color="primary" variant="contained" onClick={saveChanges}>
          Save Config
        </Button>
      </div>
    </div>
  )
}

export default RecurringTableSetup
